import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import apiClient from "../common/http.service";

export const useNotifications = (page = 1, limit = 10) => {
    const queryClient = useQueryClient();

    const notificationQuery = useQuery({
        queryKey: ["notifications", page, limit],
        queryFn: async () => {
            const params = {
                page,
                pageSize: limit,
            };

            const queryString = new URLSearchParams(params).toString();

            const response = await apiClient.get(`/pushnotification?${queryString}`);

            const payload = response?.data?.data || {};

            return {
                notifications: Array.isArray(payload.data) ? payload.data : [],
                pagination: payload.pagination || { totalRecords: 0, totalPages: 1 },
            };
        },
        keepPreviousData: true,
        staleTime: 5000,
    });

    const createMutation = useMutation({
        mutationFn: (data) => apiClient.post("/pushnotification", data),
        onSuccess: () => {
            toast.success("Notification created successfully");
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Create failed"),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => apiClient.put(`/pushnotification/${id}`, data),
        onSuccess: () => {
            toast.success("Notification updated successfully");
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Update failed"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => apiClient.delete(`/pushnotification/${id}`),
        onSuccess: () => {
            toast.success("Notification deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Delete failed"),
    });

    const sendMutation = useMutation({
        mutationFn: (data) => apiClient.post("/pushnotification/send", data),
        onSuccess: () => {
            toast.success("Push notification sent successfully!");
        },
        onError: (err) => toast.error(err.response?.data?.message || "Failed to send"),
    });

    return {
        notifications: notificationQuery.data?.notifications || [],
        pagination: notificationQuery.data?.pagination || {},
        isLoading: notificationQuery.isLoading,
        isError: notificationQuery.isError,
        error: notificationQuery.error,

        createNotification: createMutation.mutateAsync,
        isCreating: createMutation.isPending,

        updateNotification: updateMutation.mutateAsync,
        isUpdating: updateMutation.isPending,

        deleteNotification: deleteMutation.mutateAsync,
        isDeleting: deleteMutation.isPending,

        sendNotification: sendMutation.mutateAsync,
        isSending: sendMutation.isPending,
    };
};